import '@alenaksu/json-viewer';
import { Button, ButtonGroup, Tag, Text, Toggle } from '@nike/eds';

import Loader from 'components/Loader';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createAsset } from 'use-asset';

type JsonViewerElement = HTMLElementTagNameMap['json-viewer'];

const d3Resource = createAsset(() => import('d3'));
const d3GraphvizResource = createAsset(() => import('d3-graphviz'));

type ExplainStep = {
  stepName: string;
  description: string;
  stepData: object;
};

type TermProcessTrace = {
  termProcessType: string;
  descriptions: string[];
  termProcess: object;
  termProcessTraceDetails: object;
};

enum TagColor {
  Teal = 'teal',
  Blue = 'blue',
  Green = 'green',
  Yellow = 'yellow',
  Pink = 'pink',
  Orange = 'orange',
  Purple = 'purple',
}

// Define the mapping of terms to their corresponding tag colors
const TERM_COLOR_MAPPING: { [key: string]: TagColor } = {
  TERM_STYLE_COLOR: TagColor.Blue,
  TERM_NORMALIZE: TagColor.Green,
  TERM_VALID: TagColor.Pink,
  TERM_REACH: TagColor.Teal,
  TERM_TOKENIZE: TagColor.Yellow,
  TERM_NER: TagColor.Orange,
  TERM_TYPO: TagColor.Pink,
  TERM_STAGE_QUERY: TagColor.Purple,
  TERM_WEIGHT: TagColor.Orange,
  TERM_INTENTION: TagColor.Orange,
};

// Function to get a tag color by passing the term key
const getTagColorByKey = (key: string): TagColor | undefined => {
  return TERM_COLOR_MAPPING[key];
};

function Graph({ dot }: { dot: string }) {
  d3Resource.read();
  const d3Graphviz = d3GraphvizResource.read();

  useEffect(() => {
    d3Graphviz
      .graphviz('#explain-dot-graph-1', { useWorker: false })
      .width('95%' as unknown as number)
      .fit(true)
      .renderDot(dot);
  }, [d3Graphviz, dot]);

  return (
    <div
      id='explain-dot-graph-1'
      className='tw-border tw-border-gray-400 tw-rounded-lg'
    />
  );
}

// eslint-disable-next-line import/no-unused-modules
export function Title(props: {
  children: React.ReactNode;
  as?: React.ElementType;
}) {
  const children = props.children;
  const as = props.as || 'h5';
  return (
    <Text font='title-5' as={as} className='tw-my-2'>
      {children}
    </Text>
  );
}

// eslint-disable-next-line import/no-unused-modules
export function JsonViewerCustom({
  data,
  className,
}: {
  data: JsonViewerElement['data'];
  className?: string;
}) {
  const ref = useRef<JsonViewerElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.data = data;
  }, [data]);

  const copyText = useCallback(() => {
    // assuming users are using a modern browser
    navigator.clipboard
      ?.writeText(JSON.stringify(data, null, 2))
      .catch((err) => {
        console.error('navigator.clipboard.writeText Error', err);
      });
  }, [data]);
  const cx = 'tw-p-3 tw-rounded-lg' + (className ? ' ' + className : '');
  return (
    <div className={cx} style={{ backgroundColor: '#2a2f3a' }}>
      <div>
        <ButtonGroup className='eds--dark'>
          <Button
            variant='secondary'
            size='small'
            disableRipple
            onClick={() => ref.current && ref.current.expandAll()}
          >
            Expand All
          </Button>
          <Button
            variant='secondary'
            size='small'
            disableRipple
            onClick={() => ref.current && ref.current.collapseAll()}
          >
            Collapse All
          </Button>
          <Button
            variant='secondary'
            size='small'
            disableRipple
            onClick={copyText}
          >
            Copy
          </Button>
        </ButtonGroup>
      </div>
      <json-viewer id='jv-result-concepts' ref={ref} />
    </div>
  );
}

// eslint-disable-next-line import/no-unused-modules
export default function ExplainRecallDetails({
  explainRecallData,
  explainRecallIndex,
  defaultShowRecallDetailsFlag,
}: ExplainRecallDetailsProps) {
  const [showMainDetails, setShowMainDetails] = useState<boolean[]>(
    new Array(explainRecallData.mainTermProcessTraces?.length).fill(false),
  );
  const [showTermWeightFallbackDetails, setShowTermWeightFallbackDetails] =
    useState<boolean[]>(
      new Array(
        explainRecallData.termWeightFallbackTermProcessTraces?.length,
      ).fill(false),
    );
  const [
    showTermIntentionFallbackDetails,
    setShowTermIntentionFallbackDetails,
  ] = useState<boolean[]>(
    new Array(
      explainRecallData.termIntentionFallbackTermProcessTraces?.length,
    ).fill(false),
  );

  const toggleMainDetails = (index: number) => {
    const newShowDetails = [...showMainDetails];
    newShowDetails[index] = !newShowDetails[index];
    setShowMainDetails(newShowDetails);
  };
  const toggleTermWeightDetails = (index: number) => {
    const newShowDetails = [...showTermWeightFallbackDetails];
    newShowDetails[index] = !newShowDetails[index];
    setShowTermWeightFallbackDetails(newShowDetails);
  };
  const toggleTermIntentionDetails = (index: number) => {
    const newShowDetails = [...showTermIntentionFallbackDetails];
    newShowDetails[index] = !newShowDetails[index];
    setShowTermIntentionFallbackDetails(newShowDetails);
  };

  const [showDetails, setShowDetails] = useState(defaultShowRecallDetailsFlag);

  const toggleDetails = () => setShowDetails(!showDetails);

  return (
    <div>
      <section className='tw-py-2'>
        <Toggle
          label='recall details'
          id={`toggle-stage-recall-${explainRecallIndex}`}
          checked={showDetails}
          onChange={toggleDetails}
          className='tw-ml-2'
        />
      </section>

      {showDetails && (
        <div>
          <section className='tw-py-2'>
            <Title as='span'>originalSearchPhrase:</Title>
            <Text font='body-1' as='span' className='tw-ml-2'>
              {explainRecallData.originalSearchPhrase
                ? JSON.stringify(explainRecallData.originalSearchPhrase)
                : 'N/A'}
            </Text>
          </section>
          <section className='tw-py-2'>
            <Title as='span'>result.stageName:</Title>
            <Text font='body-1' as='span' className='tw-ml-2'>
              {explainRecallData.result?.stageName
                ? explainRecallData.result.stageName
                : 'N/A'}
            </Text>
          </section>
          {explainRecallData.result && explainRecallData.result.concepts ? (
            <section className='tw-py-2'>
              <Title>result.concepts</Title>
              <JsonViewerCustom data={explainRecallData.result.concepts} />
            </section>
          ) : null}
          {explainRecallData.result && explainRecallData.result.graphSpec ? (
            <section className='tw-py-2'>
              <Title>result.graphSpec</Title>
              <Suspense fallback={<Loader />}>
                <Graph dot={explainRecallData.result.graphSpec} />
              </Suspense>
            </section>
          ) : null}
          {explainRecallData.stepList ? (
            <>
              <section className='tw-py-2'>
                <Title>Steps</Title>
                <Text>Total {explainRecallData.stepList.length}</Text>
                {explainRecallData.stepList.map(
                  (step: ExplainStep, index: number) => (
                    <Text key={index}>
                      {index + 1}. {step.stepName}: {step.description}
                    </Text>
                  ),
                )}
              </section>

              {explainRecallData.stepList.map(
                (step: ExplainStep, index: number) => (
                  <section className='tw-py-2' key={index}>
                    <Title>
                      {index + 1}. {step.stepName}
                    </Title>
                    <Text>{step.description}</Text>
                    <JsonViewerCustom data={step.stepData} />
                  </section>
                ),
              )}
            </>
          ) : null}
          {explainRecallData.processTerm ? (
            <section className='tw-py-2'>
              <Title>processTerm</Title>
              <JsonViewerCustom data={explainRecallData.processTerm} />
            </section>
          ) : null}
          {explainRecallData.result &&
          explainRecallData.result.stageQueryClause ? (
            <section className='tw-py-2'>
              <Title>result.stageQueryClause</Title>
              <JsonViewerCustom
                data={explainRecallData.result.stageQueryClause}
              />
            </section>
          ) : null}
          {explainRecallData.searchParameters &&
          explainRecallData.searchParameters.elasticsearchQuery ? (
            <section className='tw-py-2'>
              <Title>searchParameters.elasticsearchQuery</Title>
              <JsonViewerCustom
                data={JSON.parse(
                  explainRecallData.searchParameters.elasticsearchQuery,
                )}
              />
            </section>
          ) : null}
          {explainRecallData.result && explainRecallData.result.elasticQuery ? (
            <section className='tw-py-2'>
              <Title>result.elasticQuery</Title>
              <JsonViewerCustom data={explainRecallData.result.elasticQuery} />
            </section>
          ) : null}
          {explainRecallData.stages ? (
            <section className='tw-py-2'>
              <Title>stages</Title>
              <JsonViewerCustom data={explainRecallData.stages} />
            </section>
          ) : null}
          {explainRecallData.mainTermProcessTraces ? (
            <>
              {explainRecallData.mainTermProcessTraces ? (
                <section className='tw-py-2'>
                  <Title>Term Process Trace</Title>
                  <Title>
                    Main Term Process Component Count:{' '}
                    {explainRecallData.mainTermProcessTraces.length}
                  </Title>
                  {explainRecallData.mainTermProcessTraces.map(
                    (termProcessTrace: TermProcessTrace, index: number) => (
                      <>
                        <div className='tw-flex tw-items-center tw-mb-2'>
                          <Tag
                            color={getTagColorByKey(
                              termProcessTrace.termProcessType,
                            )}
                            size='medium'
                            className='eds-tags-input-container tw-inline-flex tw-items-center tw-mr-2'
                          >
                            {index + 1}: {termProcessTrace.termProcessType}
                          </Tag>
                          <Toggle
                            label='details'
                            id={`toggle-main-${index}`}
                            checked={showMainDetails[index]}
                            onChange={() => toggleMainDetails(index)}
                            className='tw-ml-2'
                          />
                        </div>
                        {termProcessTrace.descriptions.map((des, index) => (
                          <div
                            className='tw-flex tw-items-center tw-mb-2'
                            key={index}
                          >
                            <Text>{des}</Text>
                          </div>
                        ))}
                        {showMainDetails[index] && (
                          <section className='tw-py-2'>
                            <JsonViewerCustom
                              data={termProcessTrace.termProcessTraceDetails}
                            />
                          </section>
                        )}
                      </>
                    ),
                  )}
                </section>
              ) : null}
              {explainRecallData.termWeightFallbackTermProcessTraces ? (
                <section className='tw-py-2'>
                  <Title>
                    Term Weight Fallback Process Component Count:{' '}
                    {
                      explainRecallData.termWeightFallbackTermProcessTraces
                        .length
                    }
                  </Title>
                  {explainRecallData.termWeightFallbackTermProcessTraces.map(
                    (termProcessTrace: TermProcessTrace, index: number) => (
                      <>
                        <div className='tw-flex tw-items-center tw-mb-2'>
                          <Tag
                            color={getTagColorByKey(
                              termProcessTrace.termProcessType,
                            )}
                            size='medium'
                            className='eds-tags-input-container tw-inline-flex tw-items-center tw-mr-2'
                          >
                            {index + 1}: {termProcessTrace.termProcessType}
                          </Tag>
                          <Toggle
                            label='details'
                            id={`toggle-termweight-${index}`}
                            checked={showTermWeightFallbackDetails[index]}
                            onChange={() => toggleTermWeightDetails(index)}
                            className='tw-ml-2'
                          />
                        </div>
                        {termProcessTrace.descriptions.map((des, index) => (
                          <div
                            className='tw-flex tw-items-center tw-mb-2'
                            key={index}
                          >
                            <Text>{des}</Text>
                          </div>
                        ))}
                        {showTermWeightFallbackDetails[index] && (
                          <section className='tw-py-2'>
                            <JsonViewerCustom
                              data={termProcessTrace.termProcessTraceDetails}
                            />
                          </section>
                        )}
                      </>
                    ),
                  )}
                </section>
              ) : null}
              {explainRecallData.termIntentionFallbackTermProcessTraces ? (
                <section className='tw-py-2'>
                  <Title>
                    Term Intention Fallback Process Component Count:{' '}
                    {
                      explainRecallData.termIntentionFallbackTermProcessTraces
                        .length
                    }
                  </Title>
                  {explainRecallData.termIntentionFallbackTermProcessTraces.map(
                    (termProcessTrace: TermProcessTrace, index: number) => (
                      <>
                        <div className='tw-flex tw-items-center tw-mb-2'>
                          <Tag
                            color={getTagColorByKey(
                              termProcessTrace.termProcessType,
                            )}
                            size='medium'
                            className='eds-tags-input-container tw-inline-flex tw-items-center tw-mr-2'
                          >
                            {index + 1}: {termProcessTrace.termProcessType}
                          </Tag>
                          <Toggle
                            label='details'
                            id={`toggle-termintention-${index}`}
                            checked={showTermIntentionFallbackDetails[index]}
                            onChange={() => toggleTermIntentionDetails(index)}
                            className='tw-ml-2'
                          />
                        </div>
                        {termProcessTrace.descriptions.map((des, index) => (
                          <div
                            className='tw-flex tw-items-center tw-mb-2'
                            key={index}
                          >
                            <Text>{des}</Text>
                          </div>
                        ))}
                        {showTermIntentionFallbackDetails[index] && (
                          <section className='tw-py-2'>
                            <JsonViewerCustom
                              data={termProcessTrace.termProcessTraceDetails}
                            />
                          </section>
                        )}
                      </>
                    ),
                  )}
                </section>
              ) : null}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
